import { createClient } from '@supabase/supabase-js';
import { authStorage } from './authStorage.mjs';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[supabase] VITE_SUPABASE_URL and/or VITE_SUPABASE_ANON_KEY are not set. ' +
    'Add them in Replit Secrets to enable authentication and data features.'
  );
}

// Auth-error callback — registered by AuthContext so it can sign the user out
// and show a toast whenever any Supabase call returns HTTP 401.
let _onAuthError = null;

/**
 * Register a handler that fires when a Supabase request returns 401
 * (expired / invalid session). Pass null to deregister.
 */
export const setAuthErrorHandler = (handler) => {
  _onAuthError = handler;
};

// Custom fetch that intercepts 401 responses from any Supabase API call.
// When an auth error is detected and a handler is registered, the handler
// is invoked so the app can clear the session and notify the user.
const customFetch = async (...args) => {
  const response = await fetch(...args);
  if (response.status === 401 && typeof _onAuthError === 'function') {
    // Use a microtask so the Supabase client can finish processing first.
    Promise.resolve().then(() => _onAuthError());
  }
  return response;
};

// Use placeholder values so createClient doesn't throw; calls will fail
// gracefully at the network level until real credentials are provided.
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-anon-key',
  {
  global: {
    fetch: customFetch,
  },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: authStorage,
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});
